import { useSyncExternalStore } from 'react'
import type { ConsentCallEndedNotice } from '@shared/calls/recording-consent'

/**
 * The pending "call ended — recording consent missing" popup. Fed live by the
 * server's END signal and, when the app missed it (backgrounded, killed, in the
 * car), by the account's pending notice on the next open. Dismissed callIds are
 * remembered on this device so a notice whose server-side clear failed is not
 * shown twice here.
 */

const DISMISSED_KEY = 'ascend-consent-call-ended-dismissed'
const MAX_DISMISSED = 20

let notice: ConsentCallEndedNotice | null = null
let consentAcceptedAck = false
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function rememberDismissed(callId: string): void {
  try {
    const next = [callId, ...readDismissed().filter((id) => id !== callId)].slice(0, MAX_DISMISSED)
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
  } catch {
    /* storage unavailable — the server-side clear still applies */
  }
}

export function getConsentCallEndedNotice(): ConsentCallEndedNotice | null {
  return notice
}

export function showConsentCallEndedNotice(next: ConsentCallEndedNotice): void {
  if (readDismissed().includes(next.callId)) return
  if (notice?.callId === next.callId) return
  notice = next
  emit()
}

/** Close the popup; returns the callId that was dismissed (for the server clear). */
export function closeConsentCallEndedNotice(): string | null {
  if (!notice) return null
  const callId = notice.callId
  rememberDismissed(callId)
  notice = null
  emit()
  return callId
}

/** Confirmation after the unconsented user accepted from this flow. */
export function setConsentAcceptedAck(next: boolean): void {
  if (consentAcceptedAck === next) return
  consentAcceptedAck = next
  emit()
}

function getConsentAcceptedAck(): boolean {
  return consentAcceptedAck
}

/** Sign-out / tests. */
export function resetConsentCallEndedNotice(): void {
  if (!notice && !consentAcceptedAck) return
  notice = null
  consentAcceptedAck = false
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useConsentCallEndedNotice(): ConsentCallEndedNotice | null {
  return useSyncExternalStore(subscribe, getConsentCallEndedNotice, getConsentCallEndedNotice)
}

export function useConsentAcceptedAck(): boolean {
  return useSyncExternalStore(subscribe, getConsentAcceptedAck, getConsentAcceptedAck)
}
